import React from 'react';
import { Loader2 } from 'lucide-react';
import { providerConfigs, type OAuthProvider } from './oauthProviderConfigs';

export type { OAuthProvider } from './oauthProviderConfigs';

interface OAuthProvidersProps {
  onProvider: (provider: OAuthProvider) => void;
  isLoading: boolean;
  loadingProvider?: OAuthProvider | null;
  providers?: OAuthProvider[];
  disabled?: boolean;
}

export const OAuthProviders: React.FC<OAuthProvidersProps> = ({
  onProvider,
  isLoading,
  loadingProvider,
  providers = ['google', 'microsoft', 'apple', 'slack', 'github', 'gitlab'],
  disabled = false,
}) => {
  const filteredProviders = providerConfigs.filter((config) => providers.includes(config.id));

  const handleProviderClick = (provider: OAuthProvider) => {
    if (disabled || isLoading) return;
    onProvider(provider);
  };

  return (
    <div className="space-y-3">
      {/* Divider */}
      <div className="relative">
        <div className="relative flex justify-center text-sm">
          <span
            className="px-4 text-sm font-medium"
            style={{
              color: 'var(--color-text-secondary)',
            }}
          >
            Or continue with
          </span>
        </div>
      </div>

      {/* Provider Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {filteredProviders.map((provider) => {
          const Icon = provider.icon;
          const isProviderLoading = isLoading && loadingProvider === provider.id;
          const isDisabled = disabled || isLoading;

          const isComingSoon = provider.comingSoon;
          const buttonDisabled = isDisabled || isComingSoon;

          return (
            <button
              key={provider.id}
              onClick={() => !isComingSoon && handleProviderClick(provider.id)}
              disabled={buttonDisabled}
              className={`
                relative flex items-center justify-center gap-3 px-4 py-3
                rounded-lg border transition-all duration-200
                focus:outline-hidden focus:ring-2 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                group
              `}
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border-default)',
                color: 'var(--color-text-primary)',
              }}
              onMouseEnter={(e) => {
                if (!buttonDisabled) {
                  e.currentTarget.style.borderColor = 'var(--color-accent-teal)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(20, 184, 166, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!buttonDisabled) {
                  e.currentTarget.style.borderColor = 'var(--color-border-default)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
              aria-label={
                isComingSoon ? `${provider.name} (Coming soon)` : `Continue with ${provider.name}`
              }
              title={isComingSoon ? 'Coming soon!' : undefined}
            >
              {/* Loading State */}
              {isProviderLoading ? (
                <Loader2
                  className="w-5 h-5 animate-spin"
                  style={{ color: 'var(--color-accent-teal)' }}
                />
              ) : (
                <Icon className="w-5 h-5" />
              )}

              {/* Provider Name */}
              <span className="text-sm font-medium">{provider.name}</span>

              {/* Coming Soon Badge */}
              {isComingSoon && (
                <span
                  className="absolute -top-2 -right-2 px-2 py-0.5 text-xs font-semibold rounded-full"
                  style={{
                    backgroundColor: 'var(--color-accent-teal)',
                    color: 'white',
                  }}
                >
                  Soon!
                </span>
              )}

              {/* Glass effect overlay */}
              <div
                className="absolute inset-0 rounded-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(20, 184, 166, 0.05) 0%, transparent 50%, rgba(20, 184, 166, 0.05) 100%)',
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Alternative: Single row layout for fewer providers */}
      {filteredProviders.length <= 2 && (
        <div className="hidden">
          <div className="flex gap-3">
            {filteredProviders.map((provider) => {
              const Icon = provider.icon;
              const isProviderLoading = isLoading && loadingProvider === provider.id;
              const isDisabled = disabled || isLoading;

              return (
                <button
                  key={provider.id}
                  onClick={() => handleProviderClick(provider.id)}
                  disabled={isDisabled}
                  className={`
                    flex-1 flex items-center justify-center gap-3 px-4 py-3 
                    rounded-lg border transition-all duration-200 
                    focus:outline-hidden focus:ring-2 focus:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                    hover:border-accent hover:shadow-lg
                  `}
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border-default)',
                    color: 'var(--color-text-primary)',
                  }}
                  aria-label={`Continue with ${provider.name}`}
                >
                  {isProviderLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-teal-500" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                  <span className="text-sm font-medium">{provider.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Provider Status Messages */}
      {isLoading && loadingProvider && (
        <div className="text-center">
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Connecting to {filteredProviders.find((p) => p.id === loadingProvider)?.name}...
          </p>
        </div>
      )}
    </div>
  );
};
